import { useEffect, useState } from "react";

// Componente para mostrar una cuota individual (editable)
const QuotaItem = ({ quota, isEditable = false, onQuotaChange, compact = false }) => {
  const [editableQuota, setEditableQuota] = useState(quota);

  const handleChange = (field, value) => {
    const updatedQuota = { ...editableQuota, [field]: value };
    setEditableQuota(updatedQuota);
    if (onQuotaChange) {
      onQuotaChange(updatedQuota);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800';
      case 'overdue': return 'bg-red-100 text-red-800';
      default: return 'bg-yellow-100 text-yellow-800';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'paid': return 'Pagado';
      case 'overdue': return 'Vencido';
      default: return 'Pendiente';
    }
  };

  // === NUEVO: detectar URLs disponibles para mostrar enlace ===
  // Ajusta los nombres de campos si tu API usa otros:
  const avisoPdfUrl   = editableQuota.aviso_pdf_url || editableQuota.pdf_url; // 👈 ajusta aquí si usas otro nombre
  const receiptPdfUrl = editableQuota.receipt_url;                             // 👈 ajusta aquí si usas otro nombre

  // Etiqueta priorizando comprobante si existe y está pagado
  const pdfUrlToShow =
    (editableQuota.status === 'paid' && receiptPdfUrl) ? receiptPdfUrl :
    (avisoPdfUrl ? avisoPdfUrl : null);

  const pdfLabel =
    (editableQuota.status === 'paid' && receiptPdfUrl) ? '🧾 Ver comprobante' :
    (avisoPdfUrl ? '📄 Ver aviso de cobro' : '');

  if (compact) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-sm transition-shadow">
        <div className="flex justify-between items-start mb-2">
          <div className="font-medium text-sm">{quota.period} {quota.year}</div>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(quota.status)}`}>
            {getStatusLabel(quota.status)}
          </span>
        </div>
        <div className="text-lg font-bold text-gray-900" title={`$${quota.amount?.toLocaleString()}`}>
          {`$${(quota.amount)}`}
        </div>
        <div className="text-xs text-gray-500">
          UF: {`$${(quota.uf_value)}`}
        </div>

        {/* NUEVO: enlace en vista compacta (si hay URL) */}
        {pdfUrlToShow && (
          <div className="mt-2">
            <a
              href={pdfUrlToShow}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#0488AF] hover:underline text-xs"
              title="Abrir PDF"
            >
              {pdfLabel}
            </a>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
      <div className="grid grid-cols-1 xl:grid-cols-5 lg:grid-cols-3 gap-4 items-center">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Período</label>
          <div className="font-medium">{editableQuota.period} {editableQuota.year}</div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Monto (CLP)</label>
          {isEditable ? (
            <input
              type="number"
              min="0"
              value={editableQuota.amount}
              onChange={(e) => {
                const value = e.target.value;
                if (/^\d*$/.test(value) || value === '') {
                  handleChange('amount', value === '' ? 0 : parseInt(value));
                }
              }}
              className="w-full p-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="0"
            />
          ) : (
            <div className="font-medium" title={`$${editableQuota.amount?.toLocaleString()}`}>
              {`$${(editableQuota.amount)}`}
            </div>
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Estado</label>
          {isEditable ? (
            <select
              value={editableQuota.status}
              onChange={(e) => handleChange('status', e.target.value)}
              className="w-full p-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="pending">Pendiente</option>
              <option value="paid">Pagado</option>
            </select>
          ) : (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(editableQuota.status)}`}>
              {getStatusLabel(editableQuota.status)}
            </span>
          )}
        </div>

        <div className="xl:block lg:hidden block">
          <label className="block text-xs font-medium text-gray-500 mb-1">Valor UF</label>
          {isEditable ? (
            <input
              type="number"
              min="0"
              step="any" // Permite decimales
              value={editableQuota.uf_value}
              onChange={(e) => {
                const value = e.target.value.replace(',', ''); // Elimina coma si llega a colarse
                handleChange('uf_value', value === '' ? '' : parseFloat(value));
              }}
              onKeyDown={(e) => {
                if (e.key === ',' || e.key === 'e' || e.key === 'E') {
                  e.preventDefault();
                }
              }}
              className="w-full p-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="0"
            />
          ) : (
            <div className="font-medium" title={`$${editableQuota.uf_value?.toLocaleString()}`}>
              {`$${(editableQuota.uf_value)}`}
            </div>
          )}
        </div>

        <div className="xl:block hidden">
          <label className="block text-xs font-medium text-gray-500 mb-1">Fecha de Pago</label>
          {isEditable && editableQuota.status === 'paid' ? (
            <input
              type="date"
              value={editableQuota.payment_date || ''}
              onChange={(e) => handleChange('payment_date', e.target.value)}
              className="w-full p-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          ) : (
            <div className="text-sm">{editableQuota.payment_date || '-'}</div>
          )}

          {/* NUEVO: Enlace al PDF (debajo de la fecha) */}
          {!isEditable && pdfUrlToShow && (
            <a
              href={pdfUrlToShow}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-2 text-[#0488AF] hover:underline text-sm"
              title="Abrir PDF"
            >
              {pdfLabel}
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuotaItem;
